import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BarChart3, BookOpen } from 'lucide-react';
import { formatMarketCap } from './screenerConstants';

const momentumClass = (m) => {
  if (m === 'bullish') return 'bg-green-500 text-white';
  if (m === 'bearish') return 'bg-red-500 text-white';
  return 'bg-gray-400 text-white';
};

const ResultRow = ({ row, isPro, onResearch, onAddJournal }) => (
  <tr className="border-b hover:bg-gray-50" data-testid={`screener-row-${row.symbol}`}>
    <td className="py-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-sm">
          {row.symbol.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-gray-900">{row.symbol}</p>
          <p className="text-xs text-gray-500 line-clamp-1 max-w-[160px]">{row.name}</p>
        </div>
      </div>
    </td>
    <td className="py-3 text-sm text-gray-600 capitalize">{row.sector}</td>
    <td className="py-3 font-medium">${row.price?.toFixed(2)}</td>
    <td className="py-3">
      <span className={`font-medium ${row.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {row.changePercent >= 0 ? '+' : ''}
        {row.changePercent?.toFixed(2)}%
      </span>
    </td>
    <td className="py-3 text-sm">{formatMarketCap(row.marketCap)}</td>
    <td className="py-3 text-sm">{row.peRatio ? row.peRatio.toFixed(1) : '—'}</td>
    <td className="py-3">
      <Badge
        variant={row.volumeRatio > 1.5 ? 'default' : 'outline'}
        className={row.volumeRatio > 1.5 ? 'bg-orange-500' : ''}
      >
        {row.volumeRatio?.toFixed(2)}×
      </Badge>
    </td>
    <td className="py-3">
      <Badge className={momentumClass(row.momentum)}>{row.momentum}</Badge>
    </td>
    <td className="py-3 text-right">
      <div className="flex justify-end gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onResearch(row)}
          title="Research"
          data-testid={`screener-research-${row.symbol}`}
        >
          <BarChart3 size={14} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-purple-600"
          onClick={() => onAddJournal(row)}
          title={isPro ? 'Add to Journal' : 'Pro only'}
          data-testid={`screener-journal-${row.symbol}`}
        >
          <BookOpen size={14} />
        </Button>
      </div>
    </td>
  </tr>
);

const ResultsTable = ({ results, loading, errorMsg, isPro, onResearch, onAddJournal, onRefresh }) => (
  <Card data-testid="screener-results-card">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle>Screening Results · {results.length} matches</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          data-testid="screener-refresh-btn"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading screener results…</div>
      ) : errorMsg ? (
        <div className="text-center py-12 text-red-600" data-testid="screener-error">
          {errorMsg}
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-12 text-gray-500" data-testid="screener-empty">
          No matches with current filters. Try a different preset or relax filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-gray-600">
                <th className="pb-3 font-medium">Symbol</th>
                <th className="pb-3 font-medium">Sector</th>
                <th className="pb-3 font-medium">Price</th>
                <th className="pb-3 font-medium">Change</th>
                <th className="pb-3 font-medium">Market Cap</th>
                <th className="pb-3 font-medium">P/E</th>
                <th className="pb-3 font-medium">Vol Ratio</th>
                <th className="pb-3 font-medium">Momentum</th>
                <th className="pb-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <ResultRow
                  key={row.symbol}
                  row={row}
                  isPro={isPro}
                  onResearch={onResearch}
                  onAddJournal={onAddJournal}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

export default ResultsTable;
